import React, { useEffect, useRef, useState } from "react";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogOut, ShieldCheck, Sparkles, Users, Info, Fingerprint, Home, CreditCard, ChevronRight, Check, X, Bell, Phone, User as UserIcon, Loader2, Moon, Sun, Camera, Trash2, Download, Upload, KeyRound, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiUpload, fileUrl } from "@/lib/api";
import { beginRegister, isWebAuthnSupported } from "@/lib/webauthn";
import { getPushStatus, isPushSupported, subscribeToPush, testPush, unsubscribeFromPush } from "@/lib/push";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/context/ThemeContext";
import PhoneInput from "@/components/PhoneInput";

import { swallow } from "@/lib/log";
export default function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const [plan, setPlan] = useState("free");
  const [usage, setUsage] = useState(null);
  const [webauthn, setWebauthn] = useState({ has_passkey: false, require_passkey_for_vault: false });
  const [push, setPush] = useState({ subscribed: false, devices: 0 });
  const [busy, setBusy] = useState(false);

  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", phone: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  const avatarInputRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);

  useEffect(() => {
    api.get("/plan").then((r) => setPlan(r.data.plan)).catch(swallow("Profile"));
    api.get("/plan/usage").then((r) => setUsage(r.data)).catch(swallow("Profile"));
    api.get("/webauthn/status").then((r) => setWebauthn(r.data)).catch(swallow("Profile"));
    getPushStatus().then(setPush).catch(swallow("Profile"));
  }, []);

  useEffect(() => {
    if (user) setProfileForm({ name: user.name || "", phone: user.phone || "" });
  }, [user]);

  const openEdit = () => {
    setProfileForm({ name: user?.name || "", phone: user?.phone || "" });
    setEditingProfile(true);
  };
  const cancelEdit = () => setEditingProfile(false);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = {};
      if ((profileForm.name || "").trim() !== (user?.name || "")) payload.name = profileForm.name.trim();
      // Always send phone (may be blank to clear)
      payload.phone = (profileForm.phone || "").trim();
      if (!payload.name && payload.phone === (user?.phone || "")) {
        setEditingProfile(false);
        return;
      }
      await api.patch("/auth/profile", payload);
      await refreshUser?.();
      toast.success("Profile updated");
      setEditingProfile(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update profile");
    } finally { setSavingProfile(false); }
  };

  const onAvatarPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Choose an image file"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await apiUpload("/auth/avatar", fd, "patch");
      await refreshUser?.();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload photo");
    } finally { setAvatarBusy(false); }
  };

  const removeAvatar = async () => {
    if (!user?.avatar_url) return;
    if (!window.confirm("Remove profile photo?")) return;
    setAvatarBusy(true);
    try {
      await api.delete("/auth/avatar");
      await refreshUser?.();
      toast.success("Profile photo removed");
    } catch (err) {
      toast.error("Failed to remove photo");
    } finally { setAvatarBusy(false); }
  };

  const enablePush = async () => {
    try {
      await subscribeToPush();
      setPush(await getPushStatus());
      toast.success("Push notifications enabled");
    } catch (e) { toast.error(e?.message || "Failed to enable"); }
  };
  const disablePush = async () => {
    try { await unsubscribeFromPush(); setPush(await getPushStatus()); toast.success("Push disabled"); }
    catch { toast.error("Failed"); }
  };
  const sendTestPush = async () => {
    try { const r = await testPush(); toast.success(`Sent ${r.sent}/${r.total} test notifications`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const enrollPasskey = async () => {
    if (!isWebAuthnSupported()) { toast.error("Passkeys not supported in this browser"); return; }
    setBusy(true);
    try {
      await beginRegister(api);
      const s = await api.get("/webauthn/status");
      setWebauthn(s.data);
      toast.success("Passkey enrolled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Enrollment failed");
    } finally { setBusy(false); }
  };

  const removePasskey = async () => {
    if (!window.confirm("Remove your passkey?")) return;
    try { await api.delete("/webauthn/passkey"); const s = await api.get("/webauthn/status"); setWebauthn(s.data); toast.success("Passkey removed"); }
    catch { toast.error("Failed"); }
  };

  const toggleRequirePasskey = async () => {
    try {
      await api.put("/webauthn/vault-settings", { require_passkey_for_vault: !webauthn.require_passkey_for_vault });
      const s = await api.get("/webauthn/status"); setWebauthn(s.data);
      toast.success("Vault settings updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Track C — Backup & Restore
  const [backupBusy, setBackupBusy] = useState(false);
  const restoreInputRef = useRef(null);

  // Phase 23.2 — Integrations (OpenAI key for Whisper)
  const [integrations, setIntegrations] = useState({ openai_api_key_set: false });
  const [openaiInput, setOpenaiInput] = useState("");
  const [openaiVisible, setOpenaiVisible] = useState(false);
  const [openaiBusy, setOpenaiBusy] = useState(false);

  useEffect(() => {
    api.get("/integrations").then((r) => setIntegrations(r.data)).catch(swallow("Profile"));
  }, []);

  const saveOpenaiKey = async () => {
    const k = openaiInput.trim();
    if (!k) { toast.error("Paste your OpenAI API key first"); return; }
    if (!(k.startsWith("sk-") && k.length > 20)) { toast.error("That doesn't look like a valid OpenAI key (starts with sk-)"); return; }
    setOpenaiBusy(true);
    try {
      const { data } = await api.put("/integrations", { openai_api_key: k });
      setIntegrations(data);
      setOpenaiInput("");
      setOpenaiVisible(false);
      toast.success("OpenAI key saved — Whisper 'Improve' unlocked for voice notes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save key");
    } finally { setOpenaiBusy(false); }
  };

  const clearOpenaiKey = async () => {
    if (!window.confirm("Remove your OpenAI API key? Whisper 'Improve transcript' will stop working.")) return;
    setOpenaiBusy(true);
    try {
      await api.delete("/integrations/openai");
      setIntegrations({ openai_api_key_set: false });
      toast.success("OpenAI key removed");
    } catch { toast.error("Failed to remove key"); }
    finally { setOpenaiBusy(false); }
  };

  const exportBackup = async () => {
    if (backupBusy) return;
    setBackupBusy(true);
    try {
      const { data } = await api.get("/backup/export");
      const total = Object.values(data.counts || {}).reduce((s, n) => s + n, 0);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `impo-backup-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded (${total} items)`);
    } catch (e) {
      toast.error("Backup failed");
    } finally {
      setBackupBusy(false);
    }
  };

  const importBackup = async (file) => {
    if (!file || backupBusy) return;
    if (!window.confirm("Restore from this backup? Existing items will be updated in place; new items will be added.")) return;
    setBackupBusy(true);
    try {
      const txt = await file.text();
      const parsed = JSON.parse(txt);
      const { data } = await api.post("/backup/import", parsed);
      let inserted = 0, updated = 0;
      Object.values(data.summary || {}).forEach((s) => {
        inserted += s.inserted || 0; updated += s.updated || 0;
      });
      toast.success(`Restored: ${inserted} new · ${updated} updated`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Restore failed — is this a valid Impo backup?";
      toast.error(msg);
    } finally {
      setBackupBusy(false);
      if (restoreInputRef.current) restoreInputRef.current.value = "";
    }
  };

  return (
    <>
      <TopBar title="Profile" subtitle="Your account & settings" showHome={false} />
      <div className="px-4 py-4 space-y-4">
        <div className="rounded-3xl border border-border bg-card p-5 flex items-center gap-4" data-testid="profile-card">
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onAvatarPick}
            data-testid="profile-avatar-input"
          />
          <button
            type="button"
            onClick={() => !avatarBusy && avatarInputRef.current?.click()}
            disabled={avatarBusy}
            className="relative h-14 w-14 rounded-2xl overflow-hidden bg-primary text-primary-foreground flex items-center justify-center font-display text-xl font-semibold group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="profile-avatar-button"
            aria-label="Change profile photo"
          >
            {user?.avatar_url ? (
              <img
                src={fileUrl(user.avatar_url)}
                alt="Profile"
                className="w-full h-full object-cover"
                data-testid="profile-avatar-image"
              />
            ) : (
              (user?.name || user?.email || "U")[0]?.toUpperCase()
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition">
              {avatarBusy ? (
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              ) : (
                <Camera className="h-5 w-5 text-white" />
              )}
            </span>
            {avatarBusy && !user?.avatar_url && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/45">
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              </span>
            )}
          </button>
          <div className="min-w-0 flex-1">
            <div className="font-display font-semibold text-lg truncate">{user?.name || "You"}</div>
            <div className="text-sm text-muted-foreground truncate">{user?.email}</div>
            {user?.phone && (
              <div className="text-xs text-muted-foreground truncate mt-0.5 flex items-center gap-1" data-testid="profile-phone-display">
                <Phone className="h-3 w-3" /> {user.phone}
              </div>
            )}
            <div className="mt-1 flex items-center gap-2">
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                className="text-[11px] text-primary hover:underline"
                data-testid="profile-avatar-change-link"
              >
                {user?.avatar_url ? "Change photo" : "Add photo"}
              </button>
              {user?.avatar_url && (
                <button
                  type="button"
                  onClick={removeAvatar}
                  className="text-[11px] text-muted-foreground hover:text-destructive inline-flex items-center gap-0.5"
                  data-testid="profile-avatar-remove-link"
                >
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              )}
            </div>
          </div>
          <Badge className="rounded-full text-[10px] capitalize">{usage?.trial_active ? `${usage.plan} · trial` : (usage?.plan || plan)}</Badge>
        </div>

        {!editingProfile ? (
          <button
            onClick={openEdit}
            className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left hover:bg-[hsl(var(--accent))] transition"
            data-testid="profile-edit-button"
          >
            <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center">
              <UserIcon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-sm">Personal details</div>
              <div className="text-xs text-muted-foreground truncate">
                {user?.phone
                  ? "Name and phone number set — tap to edit"
                  : "Add your name & mobile so checkouts skip the contact step"}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ) : (
          <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="profile-edit-card">
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-primary" />
              <div className="font-display font-semibold text-sm">Personal details</div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="profile-name" className="text-xs">Name</Label>
              <Input
                id="profile-name"
                value={profileForm.name}
                onChange={(e) => setProfileForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Your name"
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="profile-phone" className="text-xs flex items-center gap-1">
                <Phone className="h-3 w-3" /> Mobile number
              </Label>
              <PhoneInput
                value={profileForm.phone || ""}
                onChange={(v) => setProfileForm((f) => ({ ...f, phone: v }))}
                placeholder="98765 43210"
                inputTestId="profile-phone-input"
                triggerTestId="profile-phone-country"
              />
              <p className="text-[11px] text-muted-foreground">
                Used to pre-fill checkout. Pick your country and enter your mobile number. We never share this.
              </p>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button
                onClick={saveProfile}
                disabled={savingProfile}
                data-testid="profile-save-button"
                className="flex-1 h-10"
              >
                {savingProfile ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
                ) : (
                  "Save"
                )}
              </Button>
              <Button
                variant="outline"
                onClick={cancelEdit}
                disabled={savingProfile}
                data-testid="profile-cancel-button"
                className="h-10"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <button onClick={() => nav("/app/plan")} className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left hover:bg-[hsl(var(--accent))] transition" data-testid="profile-plan-link">
          <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><CreditCard className="h-5 w-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-sm">Plan & storage</div>
            {usage ? (
              <>
                <div className="text-xs text-muted-foreground truncate">
                  <span className="capitalize font-medium text-foreground">{usage.plan}</span>
                  {usage.trial_active ? " · trial" : ""} · {(usage.storage_used_mb >= 1024 ? (usage.storage_used_mb / 1024).toFixed(1) + " GB" : Math.round(usage.storage_used_mb) + " MB")} of {(usage.storage_limit_mb >= 1024 ? (usage.storage_limit_mb / 1024) + " GB" : usage.storage_limit_mb + " MB")}
                </div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                  <div className={`h-full ${usage.usage_pct >= 90 ? "bg-destructive" : usage.usage_pct >= 70 ? "bg-[hsl(var(--warning))]" : "bg-primary"}`} style={{ width: `${Math.min(100, usage.usage_pct)}%` }} />
                </div>
              </>
            ) : (
              <div className="text-xs text-muted-foreground">Current: <span className="capitalize">{plan}</span> · tap for tiers</div>
            )}
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>

        <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden" data-testid="profile-appearance-card">
          <button
            type="button"
            onClick={toggleTheme}
            className="w-full text-left p-4 flex items-center gap-3 hover:bg-[hsl(var(--accent))]"
            data-testid="profile-theme-toggle"
          >
            <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">Appearance</div>
              <div className="text-xs text-muted-foreground truncate">
                {theme === "dark" ? "Dark mode \u2014 tap to switch to light" : "Light mode \u2014 tap to switch to dark"}
              </div>
            </div>
            <Badge variant="outline" className="rounded-full text-[10px] capitalize">{theme}</Badge>
          </button>
        </div>

        <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden">
          <RowLink icon={Users} title="Digital Legacy" subtitle="Nominees & release rules" onClick={() => nav("/app/legacy")} testId="profile-nav-legacy" />
          <RowLink icon={Home} title="Family Vaults" subtitle="Share notes & documents with loved ones" onClick={() => nav("/app/household")} testId="profile-nav-household" />
        </div>

        <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="profile-passkey-card">
          <div className="flex items-center gap-2">
            <Fingerprint className="h-4 w-4 text-primary" />
            <div className="font-display font-semibold text-sm">Passkey unlock (WebAuthn)</div>
          </div>
          <p className="text-xs text-muted-foreground">Use your device's biometric/PIN to unlock the password vault. Passkeys never leave your device.</p>
          <div className="flex items-center gap-2">
            {webauthn.has_passkey ? (
              <>
                <span className="text-xs inline-flex items-center gap-1 text-primary"><Check className="h-3 w-3" /> Enrolled</span>
                <Button variant="outline" size="sm" onClick={removePasskey} data-testid="passkey-remove-button">Remove</Button>
              </>
            ) : (
              <Button size="sm" onClick={enrollPasskey} disabled={busy} data-testid="passkey-enroll-button">
                {busy ? "Enrolling…" : "Enroll passkey"}
              </Button>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="passkey-require-toggle">
            <input type="checkbox" disabled={!webauthn.has_passkey} checked={!!webauthn.require_passkey_for_vault} onChange={toggleRequirePasskey} />
            <span>Require passkey to reveal any password</span>
          </label>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="profile-push-card">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            <div className="font-display font-semibold text-sm">Push notifications</div>
          </div>
          <p className="text-xs text-muted-foreground">Get a native reminder notification even when Impo is closed. Uses Web Push (VAPID). {isPushSupported() ? "" : " Your browser doesn't support Web Push."}</p>
          <div className="flex items-center gap-2">
            {push.subscribed ? (
              <>
                <span className="text-xs inline-flex items-center gap-1 text-primary"><Check className="h-3 w-3" /> Enabled on {push.devices} device{push.devices !== 1 ? "s" : ""}</span>
                <Button variant="outline" size="sm" onClick={sendTestPush} data-testid="push-test-button">Send test</Button>
                <Button variant="outline" size="sm" onClick={disablePush} data-testid="push-disable-button">Disable</Button>
              </>
            ) : (
              <Button size="sm" onClick={enablePush} disabled={!isPushSupported()} data-testid="push-enable-button">
                Enable push notifications
              </Button>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden">
          <Row icon={ShieldCheck} title="Vault security" subtitle="Passwords encrypted with Fernet on server" />
          <RowLink icon={Sparkles} title="About Impo" subtitle="One app. Everything important." onClick={() => nav("/app/about")} testId="profile-nav-about" />
          <RowLink icon={ShieldCheck} title="Privacy Policy" subtitle="How we handle your data" onClick={() => window.open("/privacy", "_blank", "noopener")} testId="profile-nav-privacy" />
          <RowLink icon={Info} title="Terms of Service" subtitle="The rules of the road" onClick={() => window.open("/terms", "_blank", "noopener")} testId="profile-nav-terms" />
          <Row icon={Info} title="Version" subtitle="Impo v1.2 — Powered by Claude Sonnet 4.5" />
        </div>

        {/* Track C — Backup & Restore */}
        <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="profile-backup-card">
          <div className="flex items-center gap-2">
            <Download className="h-4 w-4 text-primary" />
            <div className="font-display font-semibold text-sm">Backup & restore</div>
          </div>
          <p className="text-xs text-muted-foreground">
            Export a JSON copy of everything you've saved (notes, docs metadata, encrypted passwords, journal, reminders). Restore any time by importing the file.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={exportBackup} disabled={backupBusy} data-testid="backup-export-button">
              {backupBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
              Export backup
            </Button>
            <Button size="sm" variant="outline" onClick={() => restoreInputRef.current?.click()} disabled={backupBusy} data-testid="backup-import-button">
              <Upload className="h-4 w-4 mr-1" /> Restore from file
            </Button>
            <input
              ref={restoreInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && importBackup(e.target.files[0])}
              data-testid="backup-import-input"
            />
          </div>
        </div>

        {/* Phase 23.2 — Integrations */}
        <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="profile-integrations-card">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" />
            <div className="font-display font-semibold text-sm">Integrations</div>
          </div>
          <p className="text-xs text-muted-foreground">
            Bring your own OpenAI API key to unlock Whisper transcript improvements on voice notes. Your key stays on this account and is never shared.
          </p>

          <div className="rounded-xl border border-border bg-background p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <div className="text-sm font-medium">OpenAI (Whisper)</div>
              </div>
              {integrations.openai_api_key_set ? (
                <Badge variant="secondary" className="rounded-full text-[10px]">
                  <Check className="h-3 w-3 mr-1" /> Connected
                </Badge>
              ) : (
                <Badge variant="outline" className="rounded-full text-[10px]">Not connected</Badge>
              )}
            </div>

            {!integrations.openai_api_key_set ? (
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Input
                    type={openaiVisible ? "text" : "password"}
                    placeholder="sk-..."
                    value={openaiInput}
                    onChange={(e) => setOpenaiInput(e.target.value)}
                    className="pr-9"
                    autoComplete="off"
                    data-testid="openai-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setOpenaiVisible((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 grid place-items-center text-muted-foreground hover:text-foreground"
                    aria-label={openaiVisible ? "Hide key" : "Show key"}
                    data-testid="openai-key-toggle-visible"
                  >
                    {openaiVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button
                  size="sm"
                  onClick={saveOpenaiKey}
                  disabled={openaiBusy || !openaiInput.trim()}
                  data-testid="openai-key-save"
                >
                  {openaiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex-1 text-xs text-muted-foreground">Key stored securely. Whisper Improve unlocked in voice notes.</div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearOpenaiKey}
                  disabled={openaiBusy}
                  data-testid="openai-key-remove"
                >
                  {openaiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove"}
                </Button>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground">
              Get a key at platform.openai.com → API keys. We only use it for /audio/transcriptions.
            </p>
          </div>
        </div>

        <Button variant="outline" className="w-full h-11" onClick={() => { logout(); toast.success("Signed out"); nav("/", { replace: true }); }} data-testid="profile-logout-button">
          <LogOut className="h-4 w-4 mr-1.5" /> Sign out
        </Button>
      </div>
    </>
  );
}

function Row({ icon: Icon, title, subtitle }) {
  return (
    <div className="w-full text-left p-4 flex items-center gap-3">
      <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
      </div>
    </div>
  );
}

function RowLink({ icon: Icon, title, subtitle, onClick, testId }) {
  return (
    <button onClick={onClick} className="w-full text-left p-4 flex items-center gap-3 hover:bg-[hsl(var(--accent))]" data-testid={testId}>
      <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}
